import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronRight, Settings } from 'lucide-react';
import ImageModal from './components/ImageModal';
import AnalysisResultsSection from './components/analysis/AnalysisResultsSection';
import AnalysisSettingsGrid from './components/analysis/AnalysisSettingsGrid';
import AnalysisUploadPanel from './components/analysis/AnalysisUploadPanel';
import { useAnalysisUploads } from './components/analysis/useAnalysisUploads';
import { useAuth } from './context/AuthContext';
import { useRuntimeSettings } from './context/RuntimeSettingsContext';
import {
  BOUNDING_BOX_COLORS,
  enrichPriceEstimateFields,
  getItemExplanation,
  normalizeConfidenceScore,
} from './utils/formatters';
import { resolveScraperUrl } from './utils/scraperUrl';
import { buildAnalysisDisplayImageUrls } from './utils/analysisImages';

const AnalysisPage = () => {
  const { token, user, role } = useAuth();
  const colors = BOUNDING_BOX_COLORS;
  // superuser sees the full admin view; a regular "user" gets the clean
  // Figma "user" view (no advanced settings / run details / manual name / badges).
  const isAdmin = role === 'superuser';
  const [analysisResult, setAnalysisResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const {
    modelName,
    setModelName,
    resizeMode,
    setResizeMode,
    compressionMode,
    setCompressionMode,
    useEbayRag,
    setUseEbayRag,
    useNotesRag,
    setUseNotesRag,
    assumeHiddenItems,
    setAssumeHiddenItems,
  } = useRuntimeSettings();
  const [isChangingModel, setIsChangingModel] = useState(false);
  const [isChangingEbayRag, setIsChangingEbayRag] = useState(false);
  const [isChangingNotesRag, setIsChangingNotesRag] = useState(false);
  const [isChangingAssumeHidden, setIsChangingAssumeHidden] = useState(false);
  const [originalPrices, setOriginalPrices] = useState({});
  const [originalNames, setOriginalNames] = useState({});
  const [updatingId, setUpdatingId] = useState(null);
  const [updatingNameId, setUpdatingNameId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [ebayResults, setEbayResults] = useState(null);
  const [ebayLoading, setEbayLoading] = useState(false);
  const [resultImageErrors, setResultImageErrors] = useState({});
  const {
    selectedFiles,
    previewUrls,
    isDragOver,
    fileInputRef,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    markPreviewError,
    removeFile,
  } = useAnalysisUploads({
    onFilesChanged: () => {
      setAnalysisResult(null);
    },
  });
  const scraperLink = useMemo(() => {
    const url = resolveScraperUrl(process.env.REACT_APP_SCRAPER_URL);
    if (!token || !user) return url.toString();
    url.searchParams.set('token', token);
    url.searchParams.set('user', JSON.stringify(user));
    return url.toString();
  }, [token, user]);
  const handleModelChange = (newModel) => {
    setIsChangingModel(true);
    setModelName(newModel);
    setIsChangingModel(false);
  };

  const handleEbayRagChange = (nextValue) => {
    setIsChangingEbayRag(true);
    setUseEbayRag(Boolean(nextValue));
    setIsChangingEbayRag(false);
  };

  const handleNotesRagChange = (nextValue) => {
    setIsChangingNotesRag(true);
    setUseNotesRag(Boolean(nextValue));
    setIsChangingNotesRag(false);
  };

  const handleAssumeHiddenItemsChange = (nextValue) => {
    setIsChangingAssumeHidden(true);
    setAssumeHiddenItems(Boolean(nextValue));
    setIsChangingAssumeHidden(false);
  };
  const handleRemoveFile = (index) => {
    removeFile(index);
    setResultImageErrors((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        const errorIndex = Number(key);
        if (!Number.isNaN(errorIndex)) {
          if (errorIndex < index) next[errorIndex] = value;
          if (errorIndex > index) next[errorIndex - 1] = value;
        }
      });
      return next;
    });
    if (currentImageIndex >= previewUrls.length - 1 && currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };
  const searchEbay = async (blobUrls, itemIds) => {
    setEbayLoading(true);
    setEbayResults(null);
    try {
      const response = await axios.post('/ebay_search', {
        blob_urls: blobUrls,
        item_ids: itemIds,
      });
      if (response.data.status === 'success') {
        setEbayResults(response.data.ebay_results);
      }
    } catch (error) {
      console.error('Error searching eBay:', error);
    } finally {
      setEbayLoading(false);
    }
  };
  const handleAnalyze = async () => {
    if (selectedFiles.length === 0) return;

    setLoading(true);
    setAnalysisResult(null);
    setCurrentImageIndex(0);
    setResultImageErrors({});

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('file', file);
      });
      formData.append('model', modelName);
      formData.append('resize_mode', resizeMode);
      formData.append('compression_mode', compressionMode);
      formData.append('use_ebay_rag', String(useEbayRag));
      formData.append('use_notes_rag', String(useNotesRag));
      formData.append('assume_hidden_items', String(assumeHiddenItems));

      const response = await axios.post('/analyze', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const resultData = response.data;
      if (!resultData.items && resultData.data && typeof resultData.data === 'string') {
        try {
          const cleanedJson = resultData.data.replace(/```json|```/g, '').trim();
          resultData.items = JSON.parse(cleanedJson);
        } catch (e) {
          console.error('Failed to parse data string', e);
          resultData.items = [];
        }
      } else if (!resultData.items) {
        resultData.items = resultData.data || [];
      }
      resultData.items = (Array.isArray(resultData.items) ? resultData.items : []).map((item) => {
        if (!item || typeof item !== 'object') return item;
        const explanation = getItemExplanation(item);
        const enrichedItem = enrichPriceEstimateFields(item);
        return {
          ...enrichedItem,
          explanation,
          margin_analysis: explanation,
          confidence_score: normalizeConfidenceScore(
            item.confidence_score ?? item.confidence ?? item.confidenceScore,
          ),
        };
      });

      setAnalysisResult(resultData);
      const prices = {};
      const names = {};
      resultData.items.forEach((item) => {
        if (item.id) {
          prices[item.id] = item.actual_price;
          names[item.id] = item.manual_name || '';
        }
      });
      setOriginalPrices(prices);
      setOriginalNames(names);

      if (resultData.blob_urls && resultData.blob_urls.length > 0) {
        const itemIds = (resultData.items || []).filter((i) => i.id).map((i) => i.id);
        searchEbay(resultData.blob_urls, itemIds);
      }
    } catch (error) {
      console.error('Error analyzing image:', error);
      const status = error?.response?.status;
      const serverMessage = error?.response?.data?.message;
      const serverDetail = error?.response?.data?.detail;
      const fallbackMessage = error?.message || 'Unknown error';

      let explicitError = serverDetail || serverMessage || fallbackMessage;
      if (status) {
        explicitError = `HTTP ${status}: ${explicitError}`;
      }
      if (!error?.response && error?.request) {
        explicitError = 'No response from server. Check backend availability and network connectivity.';
      }
      alert(`Error analyzing image: ${explicitError}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (id, value) => {
    setAnalysisResult((prev) => {
      if (!prev || !Array.isArray(prev.items)) return prev;
      return {
        ...prev,
        items: prev.items.map((item) => (
          item.id === id ? { ...item, actual_price: value } : item
        )),
      };
    });
  };

  const handleManualNameChange = (id, value) => {
    setAnalysisResult((prev) => {
      if (!prev || !Array.isArray(prev.items)) return prev;
      return {
        ...prev,
        items: prev.items.map((item) => (
          item.id === id ? { ...item, manual_name: value } : item
        )),
      };
    });
  };

  const updatePrice = async (id, price) => {
    setUpdatingId(id);
    try {
      await axios.post('/update_price', {
        id,
        actual_price: parseFloat(price),
      });
      setOriginalPrices((prev) => ({
        ...prev,
        [id]: parseFloat(price),
      }));
    } catch (error) {
      console.error('Error saving price:', error);
      alert('Failed to save price.');
    } finally {
      setUpdatingId(null);
    }
  };

  const updateItemName = async (id, name) => {
    setUpdatingNameId(id);
    try {
      await axios.post('/update_item_name', {
        id,
        item_name: name,
      });
      setOriginalNames((prev) => ({
        ...prev,
        [id]: name,
      }));
    } catch (error) {
      console.error('Error saving item name:', error);
      alert('Failed to save item name.');
    } finally {
      setUpdatingNameId(null);
    }
  };

  const markResultImageError = (index) => {
    setResultImageErrors((prev) => ({ ...prev, [index]: true }));
  };
  const analysisDisplayImageUrls = buildAnalysisDisplayImageUrls(
    analysisResult,
    previewUrls,
    resultImageErrors,
  );

  return (
    <div className="max-w-3xl mx-auto p-3 sm:p-6">
      <div className="bg-surface rounded-2xl shadow-sm p-4 sm:p-8">
        <h1 className="font-heading font-semibold text-[28px] sm:text-[32px] leading-tight text-center text-ink mb-2 sm:mb-3">Auction Insight Prototype</h1>
        <p className="font-body text-center text-ink/80 text-[17px] sm:text-[18px] leading-6 mb-5 sm:mb-7">Upload an auction image to identify the item and analyze margins.</p>

        <AnalysisUploadPanel
          isDragOver={isDragOver}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onOpenFilePicker={() => fileInputRef.current?.click()}
          fileInputRef={fileInputRef}
          onFileChange={handleFileChange}
          previewUrls={previewUrls}
          onMarkPreviewError={markPreviewError}
          onRemoveFile={handleRemoveFile}
          scraperLink={scraperLink}
          onAnalyze={handleAnalyze}
          loading={loading}
          selectedFileCount={selectedFiles.length}
        />

        {isAdmin && (
        <details className="mt-5 group">
          <summary className="cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink transition-colors">
            <Settings size={15} />
            Advanced settings
            <ChevronRight size={15} className="transition-transform group-open:rotate-90" />
          </summary>
          <div className="mt-3">
            <AnalysisSettingsGrid
              modelName={modelName}
              onModelChange={handleModelChange}
              isChangingModel={isChangingModel}
              resizeMode={resizeMode}
              onResizeModeChange={setResizeMode}
              compressionMode={compressionMode}
              onCompressionModeChange={setCompressionMode}
              useEbayRag={useEbayRag}
              onToggleEbayRag={handleEbayRagChange}
              isChangingEbayRag={isChangingEbayRag}
              useNotesRag={useNotesRag}
              onToggleNotesRag={handleNotesRagChange}
              isChangingNotesRag={isChangingNotesRag}
              assumeHiddenItems={assumeHiddenItems}
              onToggleAssumeHiddenItems={handleAssumeHiddenItemsChange}
              isChangingAssumeHiddenItems={isChangingAssumeHidden}
            />
          </div>
        </details>
        )}

        {analysisResult && (
          <AnalysisResultsSection
            analysisResult={analysisResult}
            modelName={modelName}
            useEbayRag={useEbayRag}
            useNotesRag={useNotesRag}
            assumeHiddenItems={assumeHiddenItems}
            analysisDisplayImageUrls={analysisDisplayImageUrls}
            currentImageIndex={currentImageIndex}
            onChangeCurrentImageIndex={setCurrentImageIndex}
            colors={colors}
            onOpenModal={() => setIsModalOpen(true)}
            onMarkResultImageError={markResultImageError}
            originalPrices={originalPrices}
            originalNames={originalNames}
            updatingId={updatingId}
            updatingNameId={updatingNameId}
            onManualNameChange={handleManualNameChange}
            onUpdateItemName={updateItemName}
            onPriceChange={handlePriceChange}
            onUpdatePrice={updatePrice}
            ebayLoading={ebayLoading}
            ebayResults={ebayResults}
            isAdmin={isAdmin}
          />
        )}
      </div>

      <ImageModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        imageUrl={analysisResult ? (analysisDisplayImageUrls[currentImageIndex] || null) : null}
        imageUrls={analysisResult ? analysisDisplayImageUrls : []}
        items={analysisResult?.items}
      />
    </div>
  );
};

export default AnalysisPage;
